/**
 * AdminPanel, správa uživatelů (jen tým/admin).
 * Admin může měnit role (client/team/admin). Tým seznam jen vidí.
 */
import React, { useEffect, useState } from 'react';
import { listUsers, setUserRole, createUser } from '../lib/apiFetch';

const ROLES = ['client', 'team', 'admin'];
const ROLE_LABEL = { client: 'Klient', team: 'Tým', admin: 'Admin' };

function fmtDate(s) {
  if (!s) return '-';
  try { return new Date(s).toLocaleDateString('cs-CZ'); } catch { return s; }
}

export function AdminPanel({ canEdit }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const load = () => {
    setError(null); setRows(null);
    listUsers().then(d => setRows(d.users || [])).catch(e => setError(e.message));
  };
  useEffect(() => { load(); }, []);

  const changeRole = async (id, role) => {
    setSavingId(id); setError(null);
    try {
      await setUserRole(id, role);
      setRows(rs => rs.map(r => (r.id === id ? { ...r, role } : r)));
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div style={{ maxWidth: 980, margin: '0 auto', padding: '1.5rem 1rem', fontFamily: "'Inter', sans-serif" }}>
      <h2 style={{ marginTop: 0, fontFamily: "'Source Serif 4', Georgia, serif", color: 'var(--star-white)', fontSize: '1.4rem' }}>
        Uživatelé
      </h2>

      {canEdit && <NewUserForm onCreated={load} />}

      {error && <div style={{ color: 'rgba(192,69,63,0.45)', marginBottom: '1rem' }}>Chyba: {error} <button onClick={load} style={linkBtn}>znovu</button></div>}
      {rows === null && !error && <p style={{ color: 'var(--text-dim)' }}>Načítání…</p>}

      {rows && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-dim)', borderBottom: '1px solid rgba(155,89,212,0.2)' }}>
                <th style={th}>E-mail</th>
                <th style={th}>Jméno</th>
                <th style={th}>Firma</th>
                <th style={th}>Role</th>
                <th style={th}>Registrace</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid rgba(155,89,212,0.08)' }}>
                  <td style={td}>{u.email}</td>
                  <td style={td}>{u.full_name || '-'}</td>
                  <td style={td}>{u.company_name || '-'}</td>
                  <td style={td}>
                    {canEdit ? (
                      <select value={u.role} disabled={savingId === u.id}
                              onChange={e => changeRole(u.id, e.target.value)} style={select}>
                        {ROLES.map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                      </select>
                    ) : (ROLE_LABEL[u.role] || u.role)}
                  </td>
                  <td style={td}>{fmtDate(u.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function NewUserForm({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: '', full_name: '', company_name: '', role: 'client' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null); // { email, temp_password }

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError(null); setCreated(null);
    try {
      const res = await createUser(form);
      setCreated(res);
      setForm({ email: '', full_name: '', company_name: '', role: 'client' });
      onCreated && onCreated();
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  if (!open) {
    return (
      <div style={{ marginBottom: '1.25rem' }}>
        <button onClick={() => { setOpen(true); setCreated(null); }} style={addBtn}>+ Nový uživatel</button>
      </div>
    );
  }

  return (
    <div style={formBox}>
      <form onSubmit={submit} style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'flex-end' }}>
        <Field label="E-mail" required>
          <input type="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} style={inp} />
        </Field>
        <Field label="Jméno">
          <input value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} style={inp} />
        </Field>
        <Field label="Firma">
          <input value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} style={inp} />
        </Field>
        <Field label="Role">
          <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} style={inp}>
            <option value="client">Klient</option>
            <option value="team">Tým</option>
            <option value="admin">Admin</option>
          </select>
        </Field>
        <button type="submit" disabled={busy} style={addBtn}>{busy ? '…' : 'Založit'}</button>
        <button type="button" onClick={() => setOpen(false)} style={{ ...addBtn, background: 'transparent', color: 'var(--purple-glow)', border: '1px solid rgba(155,89,212,0.3)' }}>Zavřít</button>
      </form>
      {error && <div style={{ color: 'rgba(192,69,63,0.45)', marginTop: '0.6rem', fontSize: '0.85rem' }}>{error}</div>}
      {created && (
        <div style={{ marginTop: '0.75rem', padding: '0.75rem 1rem', borderRadius: 8,
          background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)', fontSize: '0.85rem', color: 'rgba(47,143,91,0.35)' }}>
          Účet <strong>{created.email}</strong> založen. Dočasné heslo (předejte klientovi):{' '}
          <code style={{ color: 'var(--gold)', fontSize: '0.9rem' }}>{created.temp_password}</code>
        </div>
      )}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.75rem', color: 'var(--text-dim)' }}>
      <span>{label}{required ? ' *' : ''}</span>
      {children}
    </label>
  );
}

const addBtn = {
  padding: '0.5rem 1rem', borderRadius: 8, border: 'none',
  background: 'var(--purple-glow)', color: '#FFFFFF',
  fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
};
const formBox = {
  marginBottom: '1.25rem', padding: '1rem', borderRadius: 12,
  background: 'var(--purple-dark,#FFFFFF)', border: '1px solid rgba(0,0,0,0.15)',
};
const inp = {
  padding: '0.5rem 0.7rem', background: 'var(--purple-dark)', color: 'var(--star-white)',
  border: '1px solid rgba(155,89,212,0.3)', borderRadius: 6, fontSize: '0.85rem', minWidth: 150,
};

const th = { padding: '0.6rem 0.75rem', fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' };
const td = { padding: '0.7rem 0.75rem', color: 'var(--star-white)' };
const select = {
  background: 'var(--purple-dark)', color: 'var(--star-white)', border: '1px solid rgba(155,89,212,0.3)',
  borderRadius: 6, padding: '0.3rem 0.5rem', fontSize: '0.85rem',
};
const linkBtn = { background: 'transparent', border: 'none', color: 'var(--purple-glow)', cursor: 'pointer', textDecoration: 'underline' };
